import { useEffect, useState } from "react";

let installPrompt = null;
let installed =
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;
const listeners = new Set();

function snapshot() {
  return { canInstall: Boolean(installPrompt), installed };
}

function emit() {
  const current = snapshot();
  listeners.forEach((listener) => listener(current));
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  installPrompt = event;
  emit();
});

window.addEventListener("appinstalled", () => {
  installPrompt = null;
  installed = true;
  emit();
});

export function registerPwa() {
  if (!("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) {
    // Remove only this application's old worker/cache, never other local apps.
    void (async () => {
      const isAtlasWorker = (worker) => worker && new URL(worker.scriptURL).pathname === "/sw.js";
      const controlled = isAtlasWorker(navigator.serviceWorker.controller);
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.filter((registration) =>
        isAtlasWorker(registration.active || registration.waiting || registration.installing),
      ).map((registration) => registration.unregister()));
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.filter((key) => key.startsWith("atlas-shell-")).map((key) => caches.delete(key)));
      }
      if (controlled) window.location.reload();
    })().catch(() => null);
    return;
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => null);
  });
}

export function usePwaInstall() {
  const [state, setState] = useState(snapshot);
  useEffect(() => {
    listeners.add(setState);
    return () => listeners.delete(setState);
  }, []);
  return state;
}

export async function promptPwaInstall() {
  if (!installPrompt) return false;
  const prompt = installPrompt;
  installPrompt = null;
  emit();
  await prompt.prompt();
  const choice = await prompt.userChoice;
  return choice.outcome === "accepted";
}

export function pushSupported() {
  return Boolean(
    !import.meta.env.DEV &&
    window.isSecureContext &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window,
  );
}

export async function serviceWorkerRegistration() {
  if (import.meta.env.DEV || !("serviceWorker" in navigator)) return null;
  const current = await navigator.serviceWorker.getRegistration();
  if (!current) await navigator.serviceWorker.register("/sw.js");
  return navigator.serviceWorker.ready;
}

export function base64UrlToBytes(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replaceAll("-", "+").replaceAll("_", "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}
